/**
 * Semantic action map + kb/mouse adapters (Phase 1).
 * Never bury raw KeyW checks in player/combat systems.
 */

export type Action =
  | "moveForward"
  | "moveBack"
  | "moveLeft"
  | "moveRight"
  | "jump"
  | "sprint"
  | "interact"
  | "attack"
  | "aim"
  | "menu"
  | "hotbar1"
  | "hotbar2"
  | "hotbar3"
  | "hotbar4"
  | "hotbar5";

export type ActionState = Record<Action, boolean>;

const ALL_ACTIONS: Action[] = [
  "moveForward",
  "moveBack",
  "moveLeft",
  "moveRight",
  "jump",
  "sprint",
  "interact",
  "attack",
  "aim",
  "menu",
  "hotbar1",
  "hotbar2",
  "hotbar3",
  "hotbar4",
  "hotbar5",
];

export function emptyActions(): ActionState {
  const s = {} as ActionState;
  for (const a of ALL_ACTIONS) s[a] = false;
  return s;
}

/** Default kb bindings (code-based, layout-stable). */
export const DEFAULT_KB_BINDINGS: Record<string, Action> = {
  KeyW: "moveForward",
  KeyS: "moveBack",
  KeyA: "moveLeft",
  KeyD: "moveRight",
  Space: "jump",
  ShiftLeft: "sprint",
  ShiftRight: "sprint",
  KeyE: "interact",
  KeyF: "attack",
  KeyR: "aim",
  Escape: "menu",
  Digit1: "hotbar1",
  Digit2: "hotbar2",
  Digit3: "hotbar3",
  Digit4: "hotbar4",
  Digit5: "hotbar5",
};

export interface LookDelta {
  dx: number;
  dy: number;
}

export class InputManager {
  readonly actions: ActionState = emptyActions();
  private readonly bindings: Record<string, Action>;
  private lookX = 0;
  private lookY = 0;
  private pointerLocked = false;
  /** RMB held = free look without pointer lock (backup path). */
  private rmbDown = false;
  private readonly canvas: HTMLElement;
  private readonly onKeyDown: (e: KeyboardEvent) => void;
  private readonly onKeyUp: (e: KeyboardEvent) => void;
  private readonly onMouseMove: (e: MouseEvent) => void;
  private readonly onMouseDown: (e: MouseEvent) => void;
  private readonly onMouseUp: (e: MouseEvent) => void;
  private readonly onPointerLockChange: () => void;
  private readonly onBlur: () => void;

  /** Radians sensitivity per pixel. */
  lookSensitivity = 0.003;

  constructor(canvas: HTMLElement, bindings = DEFAULT_KB_BINDINGS) {
    this.canvas = canvas;
    this.bindings = { ...bindings };

    this.onKeyDown = (e) => {
      // Ignore browser key-repeat for movement — state is level-held.
      if (e.repeat) {
        const a = this.bindings[e.code];
        if (
          a === "moveForward" ||
          a === "moveBack" ||
          a === "moveLeft" ||
          a === "moveRight" ||
          a === "jump" ||
          a === "sprint"
        ) {
          e.preventDefault();
          return;
        }
      }
      const action = this.bindings[e.code];
      if (action) {
        this.actions[action] = true;
        if (
          action === "moveForward" ||
          action === "moveBack" ||
          action === "moveLeft" ||
          action === "moveRight" ||
          action === "jump" ||
          action === "sprint"
        ) {
          e.preventDefault();
        }
      }
    };
    this.onKeyUp = (e) => {
      const action = this.bindings[e.code];
      if (action) this.actions[action] = false;
    };
    this.onMouseMove = (e) => {
      // Pointer-lock path (preferred) OR RMB-drag backup — both work while WASD held.
      if (this.pointerLocked || this.rmbDown) {
        // movementX/Y are the right signal under pointer lock; fallback to plain deltas if 0.
        const mx = e.movementX;
        const my = e.movementY;
        this.lookX += mx;
        this.lookY += my;
      }
    };
    this.onMouseDown = (e) => {
      if (e.button === 0) this.actions.attack = true;
      if (e.button === 2) {
        this.actions.aim = true;
        this.rmbDown = true;
      }
      // Capture on LMB or RMB so look works immediately.
      if (!this.pointerLocked && (e.button === 0 || e.button === 2)) {
        this.requestLock();
      }
    };
    this.onMouseUp = (e) => {
      if (e.button === 0) this.actions.attack = false;
      if (e.button === 2) {
        this.actions.aim = false;
        this.rmbDown = false;
      }
    };
    this.onPointerLockChange = () => {
      this.pointerLocked = document.pointerLockElement === this.canvas;
    };
    this.onBlur = () => {
      // Release held moves if tab loses focus mid-sprint.
      for (const a of ALL_ACTIONS) this.actions[a] = false;
      this.rmbDown = false;
    };

    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    // document-level move so look keeps working if cursor leaves canvas mid-drag
    document.addEventListener("mousemove", this.onMouseMove);
    canvas.addEventListener("mousedown", this.onMouseDown);
    window.addEventListener("mouseup", this.onMouseUp);
    document.addEventListener("pointerlockchange", this.onPointerLockChange);
    window.addEventListener("blur", this.onBlur);
    canvas.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  private requestLock(): void {
    const el = this.canvas as HTMLElement & {
      requestPointerLock?: (opts?: { unadjustedMovement?: boolean }) => void;
    };
    try {
      // unadjustedMovement = raw mouse when supported (better while keys held)
      el.requestPointerLock?.({ unadjustedMovement: true });
    } catch {
      try {
        el.requestPointerLock?.();
      } catch {
        /* ignore — RMB drag still works */
      }
    }
  }

  get isPointerLocked(): boolean {
    return this.pointerLocked;
  }

  /** Consume look delta since last call (radians contribution). */
  consumeLook(): LookDelta {
    const dx = this.lookX * this.lookSensitivity;
    const dy = this.lookY * this.lookSensitivity;
    this.lookX = 0;
    this.lookY = 0;
    return { dx, dy };
  }

  /** Horizontal move vector in camera-local XZ (-1..1 each axis). */
  moveVector(): { x: number; z: number } {
    let x = 0;
    let z = 0;
    if (this.actions.moveForward) z -= 1;
    if (this.actions.moveBack) z += 1;
    if (this.actions.moveLeft) x -= 1;
    if (this.actions.moveRight) x += 1;
    const len = Math.hypot(x, z);
    if (len > 1e-6) {
      x /= len;
      z /= len;
    }
    return { x, z };
  }

  dispose(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    document.removeEventListener("mousemove", this.onMouseMove);
    this.canvas.removeEventListener("mousedown", this.onMouseDown);
    window.removeEventListener("mouseup", this.onMouseUp);
    document.removeEventListener("pointerlockchange", this.onPointerLockChange);
    window.removeEventListener("blur", this.onBlur);
  }
}
